import React from 'react';
import { Control, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { BankAccountFieldSet } from '@/components/shared';
import { useSalaryPermissions } from '@/features/Collaborators/hooks/useSalaryPermissions';
import { LockClosedIcon } from '@heroicons/react/24/outline';

interface BankDetailsStepProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: Control<CollaboratorFormData>;
    setValue: UseFormSetValue<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
    onNoBmgAccountChange?: (hasNoBmgAccount: boolean) => void;
}

const BankDetailsStep: React.FC<BankDetailsStepProps> = ({
    context,
    collaboratorId,
    control,
    setValue,
    watch,
    errors,
    isSubmitting = false,
    onNoBmgAccountChange
}) => {
    // Permission check for salary-related fields
    const { canViewSalary, canEditSalary, shouldShowAsDisabled } = useSalaryPermissions();

    // Estado local para indicar se o colaborador NÃO possui conta BMG
    const [noBmgAccount, setNoBmgAccount] = React.useState<boolean>(false);

    // Handler para quando o checkbox é alterado
    const handleNoBmgAccountChange = (checked: boolean) => {
        // Only allow change if user has edit permission for salary
        if (!canEditSalary && context !== 'PROFILE_UPDATE') {
            return;
        }

        setNoBmgAccount(checked);

        // Limpar campos da conta salário quando checkbox for marcado
        if (checked) {
            setValue('salaryAccount.bankCode', '');
            setValue('salaryAccount.agency', '');
            setValue('salaryAccount.account', '');
            setValue('salaryAccount.digit', '');
            setValue('salaryAccount.pixKey', '');
        }

        // Notificar componente pai sobre mudança
        onNoBmgAccountChange?.(checked);
    };

    // Determine if salary account should be hidden
    const shouldHideSalaryAccount = () => {
        // In PROFILE_UPDATE context, respect the checkbox
        if (context === 'PROFILE_UPDATE') {
            return noBmgAccount;
        }

        // In other contexts, hide if user doesn't have permission to view salary
        return !canViewSalary;
    };

    // Determine if fields should be read-only
    const isReadOnly = () => {
        // In PROFILE_UPDATE, user can edit their own info
        if (context === 'PROFILE_UPDATE') {
            return isSubmitting;
        }

        // In other contexts, check permission
        return !canEditSalary || isSubmitting;
    };

    return (
        <div className="p-4 sm:p-6 space-y-6">
            {/* Checkbox appears in PROFILE_UPDATE context */}
            {context === 'PROFILE_UPDATE' && (
                <label className="flex items-center space-x-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        checked={noBmgAccount}
                        onChange={(e) => handleNoBmgAccountChange(e.target.checked)}
                        data-testid="checkbox-no-bmg-account"
                        disabled={isSubmitting}
                    />
                    <span>Não tenho conta no BMG</span>
                </label>
            )}

            {/* Permission notice for salary account if applicable */}
            {!canViewSalary && context !== 'PROFILE_UPDATE' && (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <div className="flex items-center text-sm text-gray-600">
                        <LockClosedIcon className="h-4 w-4 mr-2" />
                        <span>
                            Conta salário ocultada - você não tem permissão para visualizar dados salariais
                        </span>
                    </div>
                </div>
            )}

            {/* Bank account fields */}
            <BankAccountFieldSet
                context={context}
                collaboratorId={collaboratorId}
                control={control}
                setValue={setValue}
                watch={watch}
                errors={errors}
                isSubmitting={isReadOnly()}
                layout="full"
                hideSalaryAccount={shouldHideSalaryAccount()}
            />

            {/* Read-only notice if user can view but not edit */}
            {canViewSalary && !canEditSalary && context !== 'PROFILE_UPDATE' && (
                <div className="text-xs text-gray-500 mt-2">
                    <span className="flex items-center">
                        <LockClosedIcon className="h-3 w-3 mr-1" />
                        Campos de conta salário em modo somente leitura
                    </span>
                </div>
            )}
        </div>
    );
};

export default BankDetailsStep;